/**
 * Parse an ISO string without validation
 * @param {string} date - date string in format yyyy-mm-dd[SPACE/T]hh:mm[...]
 * @returns {Date} parsed as a local date
 */
function parseISOSimple(date: string): Date | null {
  const matches = date.match(/^(\d{4})-(\d{2})-(\d{2})[\sTt](\d{2}):(\d{2})/)

  if (!matches) {
    return null
  }

  return new Date(
    parseInt(matches[1]),
    parseInt(matches[2]) - 1, // months are 0 based in javascript
    parseInt(matches[3]),
    parseInt(matches[4]),
    parseInt(matches[5]),
  )
}

/**
 * Format a date to an AU locale string, using a given format
 *
 * Also exists for use in templates :
 *
 * ```js
 * {{ myDate | dateFormat({some: 'format'...}) }}
 * ```
 *
 *
 * @param {string} date The Date to format
 * @param {Intl.DateTimeFormatOptions} format A format object to apply
 *
 * Format reference:
 * https://cgi.cse.unsw.edu.au/~cs2041/doc/MDN_javascript_reference/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString.html
 *
 * @returns {String} Formatted date string
 */
export const dateFormat = (
  date: string,
  format: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long' },
): string => {
  if (!date) return ''

  const parsedDateTime = parseISOSimple(date)

  if (!parsedDateTime) return ''

  return parsedDateTime.toLocaleDateString('en-AU', format)
}

export const timeFormat = (
  date: string,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' },
): string => {
  if (!date) return ''

  const parsedDateTime = parseISOSimple(date)

  if (!parsedDateTime) return ''

  return parsedDateTime.toLocaleTimeString('en-AU', options)
}
